import Table from 'legacy/lut/library/list/Table'
import { useCallback, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { GlobalPartnerTypes } from 'partner360/widgets/gp360Administration'
import { savePlanIsRequired } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/components/globalPartnerDetailsContent/components/entityManagement/api/savePlanIsRequired'
import { getColumns } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/components/globalPartnerDetailsContent/components/entityManagement/helpers/getColumns'
import {
  IEntityManagementSwitcherStateForSaving,
  TEntityManagementSwitcherField,
} from 'partner360/widgets/gp360Administration/components/globalPartnerTable/components/globalPartnerDetailsContent/components/entityManagement/models'
import { getJbpGlobalPartnerDetails } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/helpers/getJbpGlobalPartnerDetails'
import { IGlobalPartnerDetails } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/models/globalPartnerDetails'
import { TRANSLATION_KEYS } from 'partner360/widgets/gp360Administration/translations'

import styles from './EntityManagement.module.scss'

interface IEntityManagementProps {
  data: IGlobalPartnerDetails[]
  globalPartnerType: GlobalPartnerTypes
  globalPartnerId: string
}

export const EntityManagement = ({
  data,
  globalPartnerId,
  globalPartnerType,
}: IEntityManagementProps) => {
  const [isSaving, setIsSaving] = useState(false)
  const [changedSwitcherState, setChangedSwitcherState] = useState<
    IEntityManagementSwitcherStateForSaving[]
  >([])

  const onlyPartnersLevel = useMemo(() => {
    return data.filter((item) => !item.hasChildren)
  }, [data])

  const crud = getJbpGlobalPartnerDetails(globalPartnerId)

  const source = useMemo(() => {
    return new Crud<IGlobalPartnerDetails>({
      fetcher: new Static<IGlobalPartnerDetails>(onlyPartnersLevel),
      keyField: 'EntryId',
      defaultPaging: {
        page: 0,
        pageSize: 200,
      },
      defaultSort: { field: 'PartnerName', dir: 'asc' },
    })
  }, [onlyPartnersLevel])

  const onSwitchersStateChange = useCallback(
    (
      partnerId: string,
      field: TEntityManagementSwitcherField,
      initialValues: Omit<IEntityManagementSwitcherStateForSaving, 'PartnerId'>
    ) =>
      (flag: number) => {
        setChangedSwitcherState((prevState) => {
          const existingIndex = prevState.findIndex((item) => item.PartnerId === partnerId)

          if (existingIndex !== -1) {
            return prevState.map((item, index) =>
              index === existingIndex ? { ...item, [field]: flag } : item
            )
          } else {
            return [
              ...prevState,
              {
                PartnerId: partnerId,
                IsPlanRequired: field === 'IsPlanRequired' ? flag : initialValues.IsPlanRequired,
                IsActualsSumUp: field === 'IsActualsSumUp' ? flag : initialValues.IsActualsSumUp,
              },
            ]
          }
        })
      },
    []
  )
  const columns = useMemo(() => {
    const isPbm = globalPartnerType !== GlobalPartnerTypes.Distributor
    return getColumns({ isPbm, onSwitchersStateChange, changedSwitcherState })
  }, [changedSwitcherState, globalPartnerType, onSwitchersStateChange])

  const save = useCallback(async () => {
    setIsSaving(true)

    await savePlanIsRequired(globalPartnerId, changedSwitcherState)
    setIsSaving(false)
    crud.removeQueries(true)
  }, [changedSwitcherState, crud, globalPartnerId])

  return (
    <div>
      <LoaderOverlay isVisible={isSaving} />
      <Flex className={styles.root} flexDirection={'column'} alignItems={'flex-end'}>
        <Button
          disabled={!Object.keys(changedSwitcherState).length}
          onClick={save}
          className={styles.btn}
        >
          <Translation text={TRANSLATION_KEYS.globalPartnerDetails.entityManagement.save} />
        </Button>
      </Flex>
      <Table source={source} columns={columns} header />
    </div>
  )
}
